import * as borogove from "borogove";
import Fuse from "fuse.js";
import {
	Chip,
	CircularProgress,
	Fab,
	Menu,
	MenuItem,
	Dialog,
	ListItemIcon,
	useMediaQuery,
} from "@mui/material";
import UnsplashPicker, { InsertIntoApplicationUploader } from "unsplash-react";
import { QRCanvas, frontalCamera } from "qr/dom.js";
import { Virtuoso } from "react-virtuoso";
import { useDebounce } from "usehooks-ts";
import { useQueryState } from "nuqs";
import { useState, useEffect, useRef, useCallback, useContext } from "react";
import {
	Outlet,
	Navigate,
	useNavigate,
	useParams,
	useSearchParams,
	useLocation,
	useOutletContext,
	useMatch,
} from "react-router";
import {
	Button,
	AddUserButton,
	ArrowButton,
	MainContainer,
	Sidebar,
	Conversation,
	Avatar,
	ConversationHeader,
	TypingIndicator,
	Search,
} from "@chatscope/chat-ui-kit-react";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faPencil,
	faXmark,
	faMagnifyingGlassArrowRight,
	faThumbtack,
	faThumbtackSlash,
} from "@fortawesome/free-solid-svg-icons";

import { AvatarWithPlaceholder } from "./AvatarWithPlaceholder";
import { AwaitOnce } from "./AwaitOnce";
import { confirmDialog } from "./ConfirmDialog";
import { Profile } from "./Profile";
import { SettingsContext } from "./Settings";
import {
	href,
	showNotification,
	useEffectEvent,
	useChatExtras,
	isInvite,
	useChats,
} from "../util";

borogove.Config.relativeHashUri = true;

export const Chats = () => {
	const location = useLocation();
	const params = useParams();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [searchQ, setSearchQ] = useQueryState("q");
	const { persistence, client, status, setStatus, logout } = useOutletContext<{
		persistence: borogove.Persistence & borogove.persistence.KeyValueStore;
		client: borogove.Client;
		status: string;
		setStatus: (status: string) => void;
		logout: (completely: boolean) => void;
	}>();
	const { audibleNotifications, messageRequests } = useContext(SettingsContext);

	const subnav = useMatch("/accounts/:accountId/:subnav/*")?.params?.subnav;
	const showSidebar = !params.chatId && subnav;
	const showRightSidebar = !!useMatch(
		"/accounts/:accountId/:subnav/:chatId/details",
	);

	const chats = useChats(client);
	const activeChat = params.chatId
		? chats.find((c) => c.chatId === params.chatId)
		: null;
	let activeChatNotifications: "all" | "mute" | "mention" | "reply" = "all";
	if (activeChat) {
		if (activeChat.notificationsFiltered()) {
			if (activeChat.notifyReply()) {
				activeChatNotifications = "reply";
			} else if (activeChat.notifyMention()) {
				activeChatNotifications = "mention";
			} else {
				activeChatNotifications = "mute";
			}
		}
	}

	const [wallpaper, setWallpaper] = useState<string | undefined>();

	const openNotificationsRef = useRef([] as Notification[]);
	const notificationEvent = useEffectEvent((message: borogove.ChatMessage) => {
		(async () => {
			if (message.type === borogove.MessageType.MessageCall) return;
			const chat = client.getChat(message.chatId());
			if (!chat) return;

			if (
				!document.hidden &&
				document.hasFocus() &&
				message.chatId() === activeChat?.chatId
			)
				return;
			if (message.versions.length > 0) return; // No notify for edits

			if (
				!chat.notificationsFiltered() ||
				(chat.notifyMention() &&
					(message.text ?? "").indexOf(client.displayName()) >= 0) ||
				(chat.notifyReply() &&
					message.replyToMessage &&
					chat.getParticipantDetails(message.replyToMessage.senderId)?.isSelf)
			) {
				if (audibleNotifications) new Audio("/message.opus").play();
				const notif = await showNotification(client.accountId(), message);
				if (notif) openNotificationsRef.current.push(notif);
			}
		})();
	});

	const [typingUsers, setTypingUsers] = useState<Record<string, string[]>>({});
	useEffect(() => {
		const userStateToken = client.addUserStateListener(
			(userId, chatId, threadId, state) => {
				void threadId;

				if (state === borogove.UserState.Composing) {
					setTypingUsers((existing) => ({
						...existing,
						[chatId]: [...(existing[chatId] || []), userId],
					}));
				} else {
					setTypingUsers((existing) => ({
						...existing,
						[chatId]: (existing[chatId] || []).filter((id) => id !== userId),
					}));
				}
			},
		);

		const messageToken = client.addChatMessageListener(
			(message: borogove.ChatMessage) => {
				if (message.isIncoming()) notificationEvent(message);
			},
		);

		return () => {
			client.removeEventListener(userStateToken);
			client.removeEventListener(messageToken);
		};
	}, [client]);

	const { getChatExtras, setChatExtras } = useChatExtras(client.accountId());

	// Request permissions once the user is logged in and seeing the app
	useEffect(() => {
		if (navigator.storage && navigator.storage.persist) {
			navigator.storage.persist();
		}

		(async () => {
			if (Notification.requestPermission) {
				await Notification.requestPermission();
			}
		})();
	}, []);

	useEffect(() => {
		const clickHandler = (e: MouseEvent) => {
			const target =
				e.target instanceof HTMLElement ? e.target.closest("a") : null;

			if (target && target.href.startsWith("xmpp:")) {
				e.preventDefault();
				if (screen.width < 576) {
					navigate(
						href(
							"/accounts/:accountId/chats",
							{
								accountId: client.accountId(),
							},
							new URLSearchParams({ q: target.href }),
						),
					);
				} else {
					setSearchQ(target.href);
				}
			}
		};
		document.addEventListener("click", clickHandler);

		return () => {
			document.removeEventListener("click", clickHandler);
		};
	}, [setSearchQ, client, navigate]);

	const debouncedSearch = useDebounce(searchQ, 300);
	const [availableChats, setAvailableChats] = useState({
		q: "",
		chats: [] as borogove.AvailableChat[],
	});
	const [addContactAnchorEl, setAddContactAnchorEl] =
		useState<null | HTMLElement>(null);

	const [showChooseBackground, setShowChooseBackground] = useState(false);
	const [popupParticipant, setPopupParticipant] = useState<
		{ id: string; details: borogove.Participant } | undefined
	>();
	const [scanQRCode, setScanQRCode] = useState(false);
	const [qrStatus, setQRStatus] = useState("Loading camera...");
	const qrDecodeTimeout = useRef<number | null>(null);
	const qrCamera = useRef<Awaited<ReturnType<typeof frontalCamera>> | null>(
		null,
	);
	const qrScanVideo = useCallback(
		(video: HTMLVideoElement) => {
			if (qrDecodeTimeout.current) {
				clearTimeout(qrDecodeTimeout.current);
				qrDecodeTimeout.current = null;
			}
			if (qrCamera.current) {
				qrCamera.current.stop();
				qrCamera.current = null;
			}
			if (scanQRCode && video) {
				const canvas = new QRCanvas({}, { cropToSquare: false });
				frontalCamera(video).then((camera) => {
					qrCamera.current = camera;
					const checkForQR = () => {
						const data = camera.readFrame(canvas);
						if (data) {
							if (data.startsWith("xmpp:")) {
								setSearchQ(data);
								setScanQRCode(false);
							} else {
								setQRStatus("Invalid QR Code found: " + data);
							}
						} else {
							setQRStatus("No QR Code found");
						}
						qrDecodeTimeout.current = setTimeout(() => {
							checkForQR();
						}, 100);
					};
					checkForQR();
				});
			}
		},
		[scanQRCode, setSearchQ],
	);

	const startChat = (availableChat: borogove.AvailableChat, trust: boolean) => {
		const chat = client.startChat(availableChat);
		if (trust) chat.setTrusted(trust);
		chat.bookmark();
		return chat;
	};

	const conversationInfo = (c: borogove.Chat) => {
		let info: string | JSX.Element = c.preview();
		const extras = getChatExtras(c.chatId);
		if (c.chatId !== params.chatId && extras?.draftText) {
			info = "Draft: " + extras.draftText;
		}
		if (c.syncing()) {
			info = `Syncing...`;
		}
		if (c.callStatus() === borogove.calls.CallStatus.Incoming) {
			info = "📲 " + info;
		}
		if (c.callStatus() === borogove.calls.CallStatus.Ongoing) {
			info = "📞 " + info;
		}
		if ((typingUsers[c.chatId] || []).length > 0) {
			info = <TypingIndicator content={info} />;
		}

		return {
			fn: c.getDisplayName(),
			photo: c.getPhoto(),
			placeholder: c.getPlaceholder(),
			info,
		};
	};

	const chatSearch = new Fuse<borogove.Chat>(chats, {
		useExtendedSearch: true,
		keys: [
			{
				name: "fn",
				weight: 2,
				getFn: (c: borogove.Chat) => c.getDisplayName(),
			},
			"chatId",
		],
	});

	const searchChats = useEffectEvent(() => {
		const search = searchQ;
		if (search) {
			client.findAvailableChats(search, (q, chats) => {
				if (search === q) setAvailableChats({ q: q, chats: chats });
				return search !== q;
			});
		}
	});
	useEffect(() => {
		searchChats();
	}, [debouncedSearch]);

	const filteredChats = searchQ
		? chatSearch.search(searchQ).map((r) => r.item)
		: chats;

	const activeChatIsInvite = activeChat
		? isInvite(messageRequests, activeChat)
		: false;

	const renderConversations: Array<borogove.Chat | borogove.AvailableChat> = (
		searchQ && searchQ === availableChats.q
			? availableChats.chats.filter(
					(result) => !chats.find((c) => c.chatId === result.chatId),
				)
			: ([] as Array<borogove.Chat | borogove.AvailableChat>)
	).concat(filteredChats.filter((c) => !isInvite(messageRequests, c)));

	const apps = filteredChats.filter((c) => c.isApp());
	const invites = filteredChats.filter((c) => isInvite(messageRequests, c));

	const anyApps = !!chats.find((c) => c.isApp());
	const anyInvites = !!chats.find((c) => isInvite(messageRequests, c));
	const activeInTab = useRef<{ chats?: string; apps?: string }>({});
	const isMobile = useMediaQuery("(max-width: 576px)");
	useEffect(() => {
		if (
			(!anyInvites && subnav === "requests") ||
			(!anyApps && subnav === "apps")
		) {
			navigate(
				href(
					"/accounts/:accountId/chats",
					{
						accountId: client.accountId(),
					},
					searchParams,
				),
			);
		} else if (!activeChat?.chatId && !isMobile) {
			if (subnav === "chats" && activeInTab.current.chats) {
				navigate(
					href(
						"/accounts/:accountId/chats/:chatId",
						{
							accountId: client.accountId(),
							chatId: activeInTab.current.chats,
						},
						searchParams,
					),
				);
			}

			if (subnav === "apps" && activeInTab.current.apps) {
				navigate(
					href(
						"/accounts/:accountId/apps/:chatId",
						{
							accountId: client.accountId(),
							chatId: activeInTab.current.apps,
						},
						searchParams,
					),
				);
			}
		}

		const tabState = activeInTab.current;
		return () => {
			if (subnav === "chats") tabState.chats = activeChat?.chatId;
			if (subnav === "apps") tabState.apps = activeChat?.chatId;
		};
	}, [
		anyApps,
		anyInvites,
		subnav,
		activeChat?.chatId,
		client,
		navigate,
		searchParams,
		isMobile,
	]);

	return (
		<MainContainer responsive>
			<Sidebar
				position="left"
				scrollable={false}
				className={showSidebar ? "active" : ""}
			>
				<ConversationHeader>
					<ConversationHeader.Content>
						<h1>
							{subnav === "apps"
								? "Apps"
								: subnav === "requests"
									? "Message Requests"
									: "Messages"}
						</h1>
					</ConversationHeader.Content>
					<ConversationHeader.Actions>
						{subnav !== "apps" && subnav !== "requests" && (
							<Button
								title="Start new chat"
								icon={<FontAwesomeIcon icon={faPencil} />}
								onClick={(e) => setAddContactAnchorEl(e.currentTarget)}
							/>
						)}
						<Menu
							open={!!addContactAnchorEl}
							anchorEl={addContactAnchorEl}
							onClose={() => setAddContactAnchorEl(null)}
						>
							<MenuItem>
								<form
									onSubmit={(e) => {
										e.preventDefault();
										const q = e.currentTarget.querySelector("input")?.value;
										setSearchQ(q || null);
										setAddContactAnchorEl(null);
									}}
								>
									<Search placeholder="Search by name, ID" />
								</form>
							</MenuItem>
							<MenuItem
								onClick={() => {
									setScanQRCode(true);
									setAddContactAnchorEl(null);
								}}
							>
								<ListItemIcon>
									<QrCodeScannerIcon />
								</ListItemIcon>
								Scan QR Code
							</MenuItem>
						</Menu>
					</ConversationHeader.Actions>
				</ConversationHeader>
				<Search
					placeholder="Search by name, ID"
					value={searchQ || ""}
					onChange={(q) => setSearchQ(q || null)}
					onClearClick={() => setSearchQ(null)}
				/>
				{subnav !== "apps" && subnav != "requests" && (
					<nav aria-label="Chat List">
						{searchQ && searchQ.length > 0 && (
							<Conversation
								name="Search messages for"
								info={searchQ}
								onClick={() => {
									navigate(
										href(
											"/accounts/:accountId/search",
											{
												accountId: client.accountId(),
											},
											searchParams,
										),
									);
								}}
							>
								<Conversation.Operations visible>
									<Button
										title="Search messages"
										icon={
											<FontAwesomeIcon icon={faMagnifyingGlassArrowRight} />
										}
										onClick={() => {
											navigate(
												href(
													"/accounts/:accountId/search",
													{
														accountId: client.accountId(),
													},
													searchParams,
												),
											);
										}}
									/>
								</Conversation.Operations>
							</Conversation>
						)}
						<Virtuoso
							aria-label="Chat List"
							onKeyDown={(e) => {
								const active = document.activeElement as HTMLDivElement;
								const scroller = (e.target as HTMLDivElement)?.parentElement
									?.parentElement?.parentElement?.parentElement;
								if (
									e.key === "Tab" &&
									active?.dataset?.virtuosoScroller !== "true" &&
									scroller &&
									active
								) {
									if (
										scroller.getBoundingClientRect().bottom -
											active.getBoundingClientRect().bottom <
										75
									) {
										scroller.scrollBy(0, 75);
									}

									if (
										active.getBoundingClientRect().top -
											scroller.getBoundingClientRect().top <
										75
									) {
										scroller.scrollBy(0, -75);
									}
								}
							}}
							totalCount={renderConversations.length}
							itemContent={(index) => {
								const result = renderConversations[index];
								if (result instanceof borogove.AvailableChat) {
									return (
										<Conversation
											key={result.chatId}
											name={result.displayName}
											info={result.note}
											onClick={() =>
												navigate(
													href(
														"./chats/:chatId",
														{
															chatId: startChat(result, false)?.chatId,
														},
														searchParams,
													),
												)
											}
										>
											<Conversation.Operations visible>
												{!result.isChannel() && (
													<AddUserButton
														onClick={() =>
															navigate(
																href(
																	"./chats/:chatId",
																	{
																		chatId: startChat(result, true)?.chatId,
																	},
																	searchParams,
																),
															)
														}
													/>
												)}
												{result.isChannel() && (
													<ArrowButton
														direction="right"
														onClick={() =>
															navigate(
																href(
																	"./chats/:chatId",
																	{
																		chatId: startChat(result, true)?.chatId,
																	},
																	searchParams,
																),
															)
														}
													/>
												)}
											</Conversation.Operations>
										</Conversation>
									);
								}

								const c: borogove.Chat = result;

								// Helper for getting the data of the first participant
								const info = conversationInfo(c);

								return (
									<Conversation
										key={c.chatId}
										name={info.fn}
										info={info.info}
										active={params.chatId === c.chatId}
										unreadCnt={c.unreadCount()}
										accessKey={index < 10 ? "" + (index + 1) : undefined}
										style={
											invites.length > 0 &&
											index >= renderConversations.length - 1
												? { paddingBottom: "5em" }
												: {}
										}
										aria-label={info.fn}
										tabIndex={0}
										onKeyDown={(e) =>
											(e.key === "Enter" || e.key === " ") &&
											e.currentTarget.click()
										}
										onClick={() => {
											const scrollTo = getChatExtras(c.chatId)?.scrollTo;
											navigate(
												href(
													"./chats/:chatId",
													{ chatId: c.chatId },
													searchParams,
												) +
													(scrollTo || c.readUpTo()
														? `#message:${scrollTo || c.readUpTo()}`
														: ""),
											);
										}}
									>
										<AvatarWithPlaceholder
											as={Avatar}
											photoUri={info.photo}
											placeholderUri={info.placeholder}
											displayName={info.fn}
										/>
										<Conversation.Operations
											visible={c.uiState === borogove.UiState.Pinned}
										>
											<Button
												style={{
													opacity:
														c.uiState === borogove.UiState.Pinned ? 0.9 : 0.5,
												}}
												icon={<FontAwesomeIcon icon={faThumbtack} />}
												title={
													c.uiState === borogove.UiState.Pinned
														? "Unpin Chat"
														: "Pin Chat"
												}
												onClick={() => c.togglePinned()}
											/>
										</Conversation.Operations>
									</Conversation>
								);
							}}
						/>
						{invites.length > 0 && (
							<Fab
								color="secondary"
								variant="extended"
								onClick={() =>
									navigate(
										href(
											"/accounts/:accountId/requests",
											{
												accountId: client.accountId(),
											},
											searchParams,
										),
									)
								}
							>
								<Chip label={invites.length} /> Message requests{" "}
								<ArrowForward />
							</Fab>
						)}
					</nav>
				)}
				{subnav === "apps" && (
					<Virtuoso
						aria-label="App List"
						totalCount={apps.length}
						itemContent={(index) => {
							const c: borogove.Chat = apps[index];

							// Helper for getting the data of the first participant
							const info = conversationInfo(c);

							return (
								<Conversation
									key={c.chatId}
									name={info.fn}
									active={params.chatId === c.chatId}
									aria-label={info.fn}
									tabIndex={0}
									onKeyDown={(e) =>
										(e.key === "Enter" || e.key === " ") &&
										e.currentTarget.click()
									}
									onClick={() => {
										navigate(
											href(
												"./apps/:chatId",
												{ chatId: c.chatId },
												searchParams,
											),
										);
									}}
								>
									<AvatarWithPlaceholder
										as={Avatar}
										photoUri={info.photo}
										placeholderUri={info.placeholder}
										displayName={info.fn}
									/>
								</Conversation>
							);
						}}
					/>
				)}
				{subnav === "requests" && (
					<>
						<Virtuoso
							aria-label="Message Request List"
							totalCount={invites.length}
							itemContent={(index) => {
								const c: borogove.Chat = invites[index];

								// Helper for getting the data of the first participant
								const info = conversationInfo(c);

								return (
									<Conversation
										key={c.chatId}
										name={info.fn}
										info={info.info}
										active={params.chatId === c.chatId}
										style={
											index >= invites.length - 1
												? { paddingBottom: "5em" }
												: {}
										}
										aria-label={info.fn}
										tabIndex={0}
										onKeyDown={(e) =>
											(e.key === "Enter" || e.key === " ") &&
											e.currentTarget.click()
										}
										onClick={() => {
											navigate(
												href(
													"./requests/:chatId",
													{ chatId: c.chatId },
													searchParams,
												),
											);
										}}
									>
										<AvatarWithPlaceholder
											as={Avatar}
											photoUri={info.photo}
											placeholderUri={info.placeholder}
											displayName={info.fn}
										/>
									</Conversation>
								);
							}}
						/>
						<Fab
							color="error"
							variant="extended"
							onClick={() => {
								confirmDialog({
									message:
										"Do you want to report every message request as SPAM?",
									accept: () => {
										for (const invite of invites) {
											invite.block(true, invite.lastMessage);
										}
									},
									reject: () => {},
								});
							}}
						>
							Report all as SPAM
						</Fab>
					</>
				)}
			</Sidebar>

			{params.chatId && !activeChat ? (
				<Navigate
					to={href("/accounts/:accountId", { accountId: client.accountId() })}
				/>
			) : (
				<Outlet
					context={{
						active: !showSidebar && !showRightSidebar,
						isInvite: activeChatIsInvite,
						activeChat,
						persistence,
						getChatExtras,
						setChatExtras,
						client,
						status,
						setStatus,
						openNotificationsRef,
						wallpaper,
						logout,
						typingUsers: params.chatId ? typingUsers[params.chatId] || [] : [],
					}}
				/>
			)}

			{activeChat && (
				<Sidebar
					position="right"
					scrollable={false}
					className={showRightSidebar ? "active" : ""}
				>
					<Button
						icon={<FontAwesomeIcon icon={faXmark} />}
						onClick={() =>
							navigate(location.pathname.replace(/\/details$/, ""))
						}
						style={{ float: "right" }}
						title="Close sidebar"
					/>
					<h2>Chat details</h2>
					<code>{activeChat.chatId}</code>
					{activeChat instanceof borogove.Channel && (
						<p className="description">{activeChat.description()}</p>
					)}

					<section>
						<h3>Settings</h3>
						<Button
							icon={
								<FontAwesomeIcon
									icon={
										activeChat.uiState === borogove.UiState.Pinned
											? faThumbtackSlash
											: faThumbtack
									}
								/>
							}
							onClick={() => activeChat.togglePinned()}
						>
							{activeChat.uiState === borogove.UiState.Pinned
								? "Unpin Chat"
								: "Pin Chat"}
						</Button>
						<Button onClick={() => activeChat.close()}>
							{activeChat.getParticipants().length > 2
								? "Leave chat"
								: "Archive chat"}
						</Button>

						<label>
							<span>Notifications</span>
							<select
								onChange={(e) => {
									if (e.target.value === "all") {
										activeChat.setNotifications(false, true, true);
									} else if (e.target.value === "mute") {
										activeChat.setNotifications(true, false, false);
									} else if (e.target.value === "mention") {
										activeChat.setNotifications(true, true, false);
									} else if (e.target.value === "reply") {
										activeChat.setNotifications(true, true, true);
									}
								}}
							>
								<option
									value="all"
									selected={activeChatNotifications === "all"}
								>
									All Messages
								</option>
								<option
									value="mention"
									selected={activeChatNotifications === "mention"}
								>
									Mentions Only
								</option>
								<option
									value="reply"
									selected={activeChatNotifications === "reply"}
								>
									Mentions and Replies Only
								</option>
								<option
									value="mute"
									selected={activeChatNotifications === "mute"}
								>
									Mute
								</option>
							</select>
						</label>

						{activeChat.isBlocked && (
							<Button onClick={() => activeChat.unblock(true)}>Unblock</Button>
						)}
						{!activeChat.isBlocked && (
							<Button onClick={() => activeChat.block()}>Block</Button>
						)}
						{!activeChat.isBlocked && (
							<Button
								onClick={() => activeChat.block(true, activeChat.lastMessage)}
							>
								Report Spam
							</Button>
						)}
						<Button onClick={() => setShowChooseBackground(true)}>
							Set Background
						</Button>

						{activeChat.getParticipants().length > 2 && (
							<>
								<h3>Members</h3>
								<ul className="participants">
									{activeChat
										.getParticipants()
										.sort(
											new Intl.Collator(undefined, {
												numeric: true,
												sensitivity: "base",
											}).compare,
										)
										.map((p) => {
											const details = activeChat.getParticipantDetails(p);
											return (
												<li key={`participant-${p}`}>
													<Button
														onClick={() =>
															setPopupParticipant({ id: p, details })
														}
													>
														<AvatarWithPlaceholder
															photoUri={details.photoUri}
															placeholderUri={details.placeholderUri}
															size="sm"
														/>
														{details.displayName}
													</Button>
												</li>
											);
										})}
								</ul>
							</>
						)}
						{showRightSidebar && activeChat.getParticipants().length === 2 && (
							<>
								<h3>Profile</h3>
								{activeChat
									.getParticipants()
									.map((p) => activeChat.getParticipantDetails(p))
									.filter((p) => !p.isSelf)
									.map((p) => (
										<AwaitOnce
											key={activeChat.chatId}
											resolve={() => p.profile(client)}
											fallback={<CircularProgress />}
										>
											{(profile) => <Profile profile={profile} />}
										</AwaitOnce>
									))}
							</>
						)}
					</section>
				</Sidebar>
			)}

			{popupParticipant && (
				<Dialog
					key={popupParticipant.id}
					open={!!popupParticipant}
					onClose={() => setPopupParticipant(undefined)}
					id="profile-dialog"
				>
					<AvatarWithPlaceholder
						photoUri={popupParticipant.details.photoUri}
						placeholderUri={popupParticipant.details.placeholderUri}
						displayName={popupParticipant.details.displayName}
						size="fluid"
					/>

					<AwaitOnce
						resolve={() => popupParticipant.details.profile(client)}
						fallback={<CircularProgress />}
					>
						{(profile) => <Profile profile={profile} />}
					</AwaitOnce>
				</Dialog>
			)}

			<Dialog
				open={showChooseBackground}
				onClose={() => setShowChooseBackground(false)}
				fullWidth={true}
			>
				<div
					style={{
						height:
							window.innerWidth < 600
								? (window.innerWidth * 4) / 3 + "px"
								: "700px",
						maxHeight: "80vh",
						overflowY: "auto",
					}}
				>
					<UnsplashPicker
						applicationName={import.meta.env.VITE_APP_TITLE}
						accessKey={import.meta.env.VITE_UNSPLASH_ACCESS_KEY}
						preferredSize={{ width: screen.width, height: screen.height }}
						Uploader={InsertIntoApplicationUploader}
						defaultSearch="illustration abstract texture"
						onFinishedUploading={(inurl) => {
							const url =
								inurl.replace(/(?:fm|crop|cs|q)=[^&]+&?/g, "") +
								"&auto=format&fit=crop&crop=entropy&q=70";
							persistence.set(`ui#wallpaper/${activeChat?.chatId}`, url);
							setWallpaper(url);
							setShowChooseBackground(false);
						}}
					/>
				</div>
			</Dialog>

			<Dialog
				open={scanQRCode}
				onClose={() => setScanQRCode(false)}
				fullScreen={isMobile}
				onClick={() => setScanQRCode(false)}
			>
				<video ref={qrScanVideo}></video>
				<p style={{ textAlign: "center", marginTop: "1em" }}>{qrStatus}</p>
			</Dialog>
		</MainContainer>
	);
};
